import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { PermissionGate } from './PermissionGate'
import { permissionForPath } from '../lib/permissions'
import { moduleForPath, usePermissions } from '../hooks/usePermissions'

export function PermissionRouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const perm = permissionForPath(location.pathname)
  const mod = moduleForPath(location.pathname)
  const { hasModule, isSuperAdmin, loading: permissionLoading, modules } = usePermissions()

  // Always allow the denial page itself (no circular permission check).
  if (location.pathname === '/access-denied') {
    return <>{children}</>
  }

  // Super admins are platform-only — keep them out of the tenant field app.
  if (!permissionLoading && isSuperAdmin) {
    return <Navigate to="/platform-console" replace />
  }

  if (permissionLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  // Skip module gate when no modules loaded (unseeded / fetch failed).
  // Permission checks below still enforce access.
  if (
    mod &&
    !isSuperAdmin &&
    modules.size > 0 &&
    !hasModule(mod) &&
    location.pathname !== '/dashboard'
  ) {
    return <Navigate to="/access-denied" replace />
  }

  if (!perm) return <>{children}</>

  return <PermissionGate permission={perm}>{children}</PermissionGate>
}